/**
 * Resolve APS user profile after PKCE login (id_token → userinfo → userprofile).
 */
import { getApsAuthBase } from "./apsConfig";

export interface VeraUserProfile {
  userId?: string;
  displayName: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  userName?: string;
  picture?: string | null;
  locale?: string;
  language?: string;
  countryCode?: string;
  jobTitle?: string;
  company?: string;
  industry?: string;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** APS returns default silhouette URLs when the user has no custom photo uploaded. */
export function isDefaultApsProfilePicture(
  url: string | null | undefined
): boolean {
  if (!url) return true;
  if (url.includes("/default/")) return true;
  return /\/user_X\d+\.(png|jpg|webp)$/i.test(url);
}

function normalizePicture(url: string | undefined): string | null {
  if (!url || isDefaultApsProfilePicture(url)) return null;
  return url;
}

function mergeProfiles(
  a: VeraUserProfile,
  b: VeraUserProfile
): VeraUserProfile {
  const pickName = (x: VeraUserProfile) =>
    x.displayName !== "Autodesk user" ? x.displayName : "";
  return {
    userId: a.userId ?? b.userId,
    displayName: pickName(a) || pickName(b) || a.displayName || b.displayName,
    email: a.email ?? b.email,
    firstName: a.firstName ?? b.firstName,
    lastName: a.lastName ?? b.lastName,
    userName: a.userName ?? b.userName,
    picture: a.picture ?? b.picture ?? null,
    locale: a.locale ?? b.locale,
    language: a.language ?? b.language,
    countryCode: a.countryCode ?? b.countryCode,
    jobTitle: a.jobTitle ?? b.jobTitle,
    company: a.company ?? b.company,
    industry: a.industry ?? b.industry,
  };
}

function unwrapApiBody(json: Record<string, unknown>): Record<string, unknown> {
  const data = json.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return json;
}

function mapOidcClaims(data: Record<string, unknown>): VeraUserProfile {
  const firstName = str(data.given_name) ?? str(data.firstName);
  const lastName = str(data.family_name) ?? str(data.lastName);
  const email = str(data.email);
  const displayName =
    [firstName, lastName].filter(Boolean).join(" ") ||
    str(data.name) ||
    str(data.preferred_username) ||
    email ||
    "Autodesk user";
  return {
    userId: str(data.sub) ?? str(data.userid) ?? str(data.user_id),
    displayName,
    email,
    firstName,
    lastName,
    userName: str(data.preferred_username) ?? str(data.username),
    picture: normalizePicture(
      data.picture === null ? undefined : str(data.picture)
    ),
    locale: str(data.locale),
    language: str(data.language),
    countryCode: str(data.country_code) ?? str(data.country),
    jobTitle: str(data.job_title),
    company: str(data.company),
    industry: str(data.industry),
  };
}

function mapUserprofileMe(data: Record<string, unknown>): VeraUserProfile {
  const body = unwrapApiBody(data);
  const firstName = str(body.firstName) ?? str(body.givenName);
  const lastName = str(body.lastName) ?? str(body.familyName);
  const email = str(body.emailId) ?? str(body.email);
  const displayName =
    str(body.name) ||
    [firstName, lastName].filter(Boolean).join(" ") ||
    email ||
    str(body.userName) ||
    "Autodesk user";
  const images = body.profileImages as Record<string, unknown> | undefined;
  const picture = normalizePicture(
    str(images?.sizeX160) ??
      str(images?.sizeX80) ??
      str(images?.sizeX40) ??
      str(images?.sizeX20) ??
      str(body.profileImageUrl)
  );
  return {
    userId: str(body.userId) ?? str(body.id),
    displayName,
    email,
    firstName,
    lastName,
    userName: str(body.userName),
    picture,
    locale: str(body.locale),
    language: str(body.language),
    countryCode: str(body.country) ?? str(body.countryCode),
    jobTitle: str(body.jobTitle) ?? str(body.job_title),
    company: str(body.company),
    industry: str(body.industry),
  };
}

/** Decode OIDC id_token or access_token JWT payload (display only — not verified). */
export function profileFromJwt(token: string): VeraUserProfile | null {
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    ) as Record<string, unknown>;
    return mapOidcClaims(payload);
  } catch {
    return null;
  }
}

async function fetchUserinfo(
  base: string,
  headers: Record<string, string>
): Promise<VeraUserProfile | null> {
  const paths = [`${base}/authentication/v2/userinfo`, `${base}/userinfo`];
  for (const url of paths) {
    try {
      const res = await fetch(url, { headers });
      if (res.ok) {
        return mapOidcClaims((await res.json()) as Record<string, unknown>);
      }
    } catch {
      /* try next */
    }
  }
  return null;
}

export async function fetchUserProfile(
  accessToken: string,
  idToken?: string
): Promise<VeraUserProfile> {
  const base = getApsAuthBase();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${accessToken}`,
  };

  let profile: VeraUserProfile = { displayName: "Autodesk user" };

  if (idToken) {
    const fromId = profileFromJwt(idToken);
    if (fromId) profile = mergeProfiles(profile, fromId);
  }

  const fromJwt = profileFromJwt(accessToken);
  if (fromJwt) profile = mergeProfiles(profile, fromJwt);

  const userinfo = await fetchUserinfo(base, headers);
  if (userinfo) profile = mergeProfiles(profile, userinfo);

  try {
    const me = await fetch(`${base}/userprofile/v1/users/@me`, { headers });
    if (me.ok) {
      profile = mergeProfiles(
        profile,
        mapUserprofileMe((await me.json()) as Record<string, unknown>)
      );
    }
  } catch {
    /* continue */
  }

  return profile;
}

export function profileInitials(profile: VeraUserProfile): string {
  if (profile.firstName && profile.lastName) {
    return `${profile.firstName[0]}${profile.lastName[0]}`.toUpperCase();
  }
  const parts = profile.displayName.trim().split(/\s+/);
  if (parts.length >= 2 && profile.displayName !== "Autodesk user") {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (profile.email) return profile.email.slice(0, 2).toUpperCase();
  return profile.displayName.slice(0, 2).toUpperCase() || "?";
}

/** Rows for the account menu — only defined fields. */
export function profileDetailRows(
  profile: VeraUserProfile
): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [];
  if (profile.firstName)
    rows.push({ label: "First name", value: profile.firstName });
  if (profile.lastName)
    rows.push({ label: "Last name", value: profile.lastName });
  if (profile.email) rows.push({ label: "Email", value: profile.email });
  if (profile.userName && profile.userName !== profile.email) {
    rows.push({ label: "Username", value: profile.userName });
  }
  if (profile.company) rows.push({ label: "Company", value: profile.company });
  if (profile.jobTitle)
    rows.push({ label: "Job title", value: profile.jobTitle });
  if (profile.industry)
    rows.push({ label: "Industry", value: profile.industry });
  if (profile.locale) rows.push({ label: "Locale", value: profile.locale });
  if (profile.language)
    rows.push({ label: "Language", value: profile.language });
  if (profile.countryCode)
    rows.push({ label: "Country", value: profile.countryCode });
  return rows;
}
